import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import CreateOwnerModal from './CreateOwnerModal';
import CreateTechnicianModal from './CreateTechnicianModal';
import CreateEmployeeModal from './CreateEmployeeModal';

import {
  ClientFormValues,
  clientManageSchema,
  ClientFormValuesWithObject,
  SiteFormValues,
  siteFormSchema,
} from 'api/clients/types';
import useCreateClientWithObject from 'hooks/Clients/useCreateClientWithObject';
import useGetClientOwners from 'hooks/Clients/useGetClientOwners';
import useGetAvailableTechnicians from 'hooks/Object/useGetAvailableTechnicians';
import useGetClientsOnMap from 'hooks/Clients/useGetClientsOnMap';
import { useCreateTechnicianForClient } from 'hooks/Clients/smartUsersForClientCreate/useCreateTechnicianForClient';
import { MLInput } from 'components/Input/Input';
import { countries } from 'api/clients/country-list';
import Toggle from 'components/Common/Toggle';
import { MLSelect } from 'components/Select/Select';
import GreenAddButton from 'components/Button/GreenAddButton';
import { useGetAvailableManagers } from 'hooks/Object/useGetAvailableManager';
import useCreateSite from 'hooks/Object/useCreateSite';

interface ChooseModalProps {
  clientId?: number;
  isOpen: boolean;
  onClose: () => void;
  refetchClients: () => void;
  currentTab: 'clientCreation' | 'technicianCreation' | 'ownerCreation';
}

interface NewTechnician {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirm_password: string;
}

type SiteForm = SiteFormValues & {
  // only for inline creation mode
  newTechnician?: NewTechnician;
};

const objectDefaultValues: SiteFormValues = {
  name: '',
  city: '',
  street: '',
  building_number: '',
  full_name: '',
  apartment_no: '',
  company: 0,
  technicians: [],
  manager: 0,
  country: '',
  zip_code: '',
};

const ChooseModal = ({ clientId, isOpen, refetchClients, onClose, currentTab }: ChooseModalProps) => {
  const { t } = useTranslation();

  const { data: clientsData } = useGetClientsOnMap();
  const { clientOwners, refetch: refetchClientOwners } = useGetClientOwners();
  const { data: availableTechnicians, refetch: refetchAvailableTechnicians } = useGetAvailableTechnicians();
  const { data: availableManagers, refetch: refetchAvailableManagers } = useGetAvailableManagers();

  const { createNewClientWithObject, isLoading: isCreatingClient } = useCreateClientWithObject();
  const { createSite, isLoading: isCreatingSite } = useCreateSite();
  // for disabling create site button (you had it)
  const { isLoading: isCreatingTechnician } = useCreateTechnicianForClient();

  const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
  const [isTechModalOpen, setIsTechModalOpen] = useState(false);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);

  const [isAddTechnician, setIsAddTechnician] = useState<boolean>(false);
  const [isObjectCreating, setIsObjectCreating] = useState<boolean>(false);
  const SNACKBAR_OPTS_ERROR = {
    variant: 'error' as const,
    style: { marginTop: '-10px' },
  };
  const SNACKBAR_OPTS_SUCCESS = {
    variant: 'success' as const,
    style: { marginTop: '-10px' },
  };
  // ---------------- options ----------------
  const clientOptions = useMemo(() => {
    if (!clientsData || clientsData.length === 0) return [];
    return clientsData.map((client: any) => ({
      value: String(client.id),
      label: client.name,
    }));
  }, [clientsData]);

  const ownerOptions = useMemo(() => {
    if (!clientOwners || clientOwners.length === 0) {
      return [{ value: '', label: t('Modals.manage-object.noOwnersToSet') }];
    }
    return [{ value: '', label: t('Modals.manage-object.select-owner') }].concat(
      clientOwners.map(owner => ({
        value: String(owner.id),
        label: owner.last_name ? `${owner.last_name} ${owner.first_name}` : owner.email,
      })),
    );
  }, [clientOwners, t]);

  const technicianOptions = useMemo(() => {
    if (!availableTechnicians || availableTechnicians.length === 0) {
      return [{ value: '', label: t('Modals.manage-client.noTechniciansAvailable') }];
    }
    return [{ value: '', label: t('Modals.manage-object.selectTechnician') }].concat(
      availableTechnicians.map(tech => ({
        value: String(tech.id),
        label: tech.label || tech.email,
      })),
    );
  }, [availableTechnicians, t]);
  const managerOptions = useMemo(() => {
    if (!availableManagers || availableManagers.length === 0) {
      return [{ value: '', label: t('Modals.manage-client.noManagersAvailable') }];
    }
    return [{ value: '', label: t('Modals.manage-object.selectManager') }].concat(
      availableManagers.map(manager => ({
        value: String(manager.id),
        label: manager.first_name && manager.last_name ? `${manager.first_name} ${manager.last_name}` : manager.email,
      })),
    );
  }, [availableManagers, t]);
  const countryList = useMemo(() => {
    if (countries.length === 0 || !countries) {
      return [{ value: '', label: t('Modals.dont-have-country') }];
    }
    return [{ value: '', label: t('Modals.country-select') }].concat(
      countries.map(country => {
        return {
          value: country.countryName,
          label: country.countryName,
          flagImg: country.flagImg,
        };
      }),
    );
  }, [t]);
  const formatCountryOption = (option: any) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {option.flagImg && (
        <img src={option.flagImg} alt={option.label} style={{ width: '20px', height: '15px', display: 'block' }} />
      )}
      <span>{option.label}</span>
    </div>
  );
  // ---------------- schemas ----------------
  // Your objectSchema expects: company: number|null, owner: number|null, technicians: number[]
  const dynamicSiteSchema = useMemo(() => {
    const base = siteFormSchema;

    if (!isAddTechnician) {
      // choose existing tech => require at least 1 technician id inside technicians[]
      return base.extend({
        technicians: z.array(z.number()).min(1, { message: t('Modals.manage-object.fieldRequired') as any }),
      });
    }

    // inline create => technicians[] optional, but newTechnician required
    return base.extend({
      technicians: z.array(z.number()).optional().default([]),
      newTechnician: z
        .object({
          first_name: z.string().min(1, t('Modals.manage-object.fieldRequired') as any),
          last_name: z.string().min(1, t('Modals.manage-object.fieldRequired') as any),
          email: z.string().email(t('Invalid email address' as any)),
          password: z.string().min(6, t('Password is too short' as any)),
          confirm_password: z.string().min(6),
        })
        .refine(d => d.password === d.confirm_password, {
          message: t("Passwords don't match" as any),
          path: ['confirm_password'],
        }),
    });
  }, [isAddTechnician, t]);
  const formSchema = useMemo(() => {
    if (!isObjectCreating && !clientId) {
      return clientManageSchema.extend({
        country: z.string().optional(),
        city: z.string().optional(),
        zip_code: z.string().optional(),
        street: z.string().optional(),
        building_no: z.string().optional(),
        technician: z.string().optional(),
      });
    }
    return clientManageSchema;
  }, [isObjectCreating, clientId]);
  // ---------------- forms ----------------
  const {
    handleSubmit: handleSiteSubmit,
    control: siteControl,
    reset: resetSite,
    setError: setSiteError,
    setValue: setSiteValue,
  } = useForm<SiteForm>({
    resolver: zodResolver(dynamicSiteSchema),
    defaultValues: {
      ...objectDefaultValues,
      company: clientId,
      newTechnician: {
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        confirm_password: '',
      },
    },
  });

  const {
    handleSubmit: handleClientSubmit,
    control: clientControl,
    reset: resetClient,
    setError: setClientError,
  } = useForm<ClientFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      EDRPOU: '',
      business_structure: '',
      country: '',
      city: '',
      zip_code: '',
      street: '',
      building_no: '',
      apartment_no: '',
      owner: '',
      image: null as any,
      technician: '',
      manager: null,
    },
  });

  // reset site defaults whenever clientId changes (ManageObjectModal style)
  useEffect(() => {
    resetSite({
      ...objectDefaultValues,
      company: clientId,
      technicians: [],
      newTechnician: {
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        confirm_password: '',
      },
    });
  }, [clientId, resetSite]);

  // when switching inline mode, clear conflicting fields
  useEffect(() => {
    if (isAddTechnician) {
      // inline -> no selected technicians
      setSiteValue('technicians', [], { shouldValidate: true });
    } else {
      // selectig -> clear inline
      setSiteValue('newTechnician', {
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        confirm_password: '',
      });
    }
  }, [isAddTechnician, setSiteValue]);

  const onResetAll = () => {
    resetClient();
    resetSite({
      ...objectDefaultValues,
      company: clientId,
      technicians: [],
      newTechnician: {
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        confirm_password: '',
      },
    });

    setIsObjectCreating(false);
    setIsAddTechnician(false);
  };

  // ---------------- submit handlers ----------------
  const onCreateNewSite = (form: SiteForm) => {
    const payload: any = {
      ...form,
      full_name: form.name,
      company: form.company ?? clientId ?? null,
      isAddTechnician,
    };

    if (isAddTechnician) {
      payload.technician = form.newTechnician;
    }

    delete payload.newTechnician;

    createSite(payload, {
      onSuccess: () => {
        enqueueSnackbar(t('Modals.add-create-site', { technicianName: payload.name }), SNACKBAR_OPTS_SUCCESS);
        onResetAll();
        onClose();
        refetchClients();
      },
      onError: (error: any) => {
        if (error?.response?.data?.name) {
          setSiteError('name', { message: t('Modals.manage-object.nameAlreadyExists') });
        } else {
          enqueueSnackbar(t('Modals.error', 'Something went wrong'), SNACKBAR_OPTS_ERROR);
        }
      },
    });
  };

  const onCreateNewClient = (data: ClientFormValues) => {
    const ownerId = typeof data.owner === 'string' && data.owner ? parseInt(data.owner, 10) : null;
    const technicianId = data.technician ? parseInt(String(data.technician), 10) : null;

    const clientData: ClientFormValuesWithObject = {
      name: data.name,
      EDRPOU: data.EDRPOU || '',
      business_structure: data.business_structure || '',
      country: data.country || '',
      city: data.city || '',
      zip_code: data.zip_code || '',
      street: data.street || '',
      building_no: data.building_no || '',
      apartment_no: data.apartment_no || '',
      full_name: data.name,
      owner: ownerId,
      technician: isObjectCreating ? technicianId : null,
    };

    createNewClientWithObject(
      { client: clientData, isObjectCreating },
      {
        onSuccess: () => {
          enqueueSnackbar(t('Modals.add-create-client', { ownerName: data.name || '—' }), SNACKBAR_OPTS_SUCCESS);
          onResetAll();
          onClose();
          refetchClients();
        },
        onError: (error: any) => handleCreateError(error),
      },
    );
  };

  const handleCreateError = (error: any) => {
    if (error?.response?.data?.name) {
      setClientError('name', { message: t('Modals.manage-object.nameAlreadyExists') });
    }
    if (error?.response?.data?.EDRPOU) {
      setClientError('EDRPOU', { message: t('Modals.manage-client.edrpouError') });
    }
  };

  // ---------------- UI ----------------
  const renderTechnicianCreationTab = () => (
    <form onSubmit={e => e.preventDefault()}>
      <div className="row border-bottom pb-2 mb-3 mt-2">
        <div className="col-12">
          <h5 className="text-muted mb-0 fs-15">{t('Modals.manage-object.create.title', 'Create Site')}</h5>
        </div>
      </div>

      <div className="row">
        <div className="mb-3 col-md-12">
          <Controller
            control={siteControl}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <MLInput
                type="text"
                label={t('Modals.manage-client.short-name')}
                error={error?.message}
                required
                {...field}
              />
            )}
          />
        </div>
      </div>

      <div className="row">
        <div className="mb-3 col-md-12">
          <Controller
            control={siteControl}
            name="company"
            render={({ field, fieldState: { error } }) => (
              <MLSelect
                {...field}
                required
                label={t('Modals.manage-object.company', 'Company')}
                error={error?.message}
                isDisabled={!!clientId}
                options={clientOptions}
                value={field.value != null ? String(field.value) : ''}
                onChange={val => field.onChange(val ? Number(val) : null)}
              />
            )}
          />
          {clientId && <small className="text-muted">Creating site for current client</small>}
        </div>
      </div>

      <div className="row border-bottom pb-2 mb-3 mt-3">
        <div className="col-12">
          <h5 className="text-muted mb-0 fs-15">{t('Modals.manage-client.address')}</h5>
        </div>
      </div>

      <div className="row">
        <div className="mb-3 col-md-4">
          <Controller
            control={siteControl}
            name="country"
            render={({ field, fieldState: { error } }) => (
              <MLSelect
                {...field}
                label={t('Modals.manage-client.country')}
                error={error?.message}
                options={countryList}
                formatOptionLabel={formatCountryOption}
                value={String(field.value || '')}
                onChange={val => field.onChange(val || '')}
                required
              />
            )}
          />
        </div>
        <div className="mb-3 col-md-4">
          <Controller
            control={siteControl}
            name="city"
            render={({ field, fieldState: { error } }) => (
              <MLInput type="text" label={t('Modals.manage-client.city')} error={error?.message} required {...field} />
            )}
          />
        </div>
        <div className="mb-3 col-md-4">
          <Controller
            control={siteControl}
            name="zip_code"
            render={({ field, fieldState: { error } }) => (
              <MLInput
                type="text"
                label={t('Modals.manage-client.zip')}
                error={error?.message}
                required
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </div>
      </div>

      <div className="row">
        <div className="mb-3 col-md-4">
          <Controller
            control={siteControl}
            name="street"
            render={({ field, fieldState: { error } }) => (
              <MLInput
                type="text"
                label={t('Modals.manage-client.street')}
                error={error?.message}
                required
                {...field}
              />
            )}
          />
        </div>

        <div className="mb-3 col-md-4">
          <Controller
            control={siteControl}
            name="building_number"
            render={({ field, fieldState: { error } }) => (
              <MLInput
                type="text"
                label={t('Modals.manage-client.building_no')}
                error={error?.message}
                required
                {...field}
              />
            )}
          />
        </div>

        <div className="mb-3 col-md-4">
          <Controller
            control={siteControl}
            name="apartment_no"
            render={({ field, fieldState: { error } }) => (
              <MLInput type="text" label={t('Modals.manage-client.apartment_no')} error={error?.message} {...field} />
            )}
          />
        </div>
      </div>

      <div className="row border-bottom pb-2 mb-3 mt-3">
        <div className="col-12">
          <h5 className="text-muted mb-0 fs-15">{t('Modals.manage-client.persons')}</h5>
        </div>
      </div>

      <div className="row">
        <div className="mb-3 col-md-11">
          <Controller
            control={siteControl}
            name="technicians"
            render={({ field, fieldState: { error } }) => (
              <MLSelect
                required={!isAddTechnician}
                isDisabled={isAddTechnician}
                label={t('Modals.manage-technician.technician')}
                error={error?.message}
                options={technicianOptions}
                value={field.value?.[0] != null ? String(field.value[0]) : ''}
                onChange={val => field.onChange(val ? [Number(val)] : [])}
              />
            )}
          />
        </div>
        <div className="mb-3 col-md-1 mt-auto">
          <GreenAddButton
            title={t('Modals.manage-client.createNewTechnician')}
            onClick={() => setIsTechModalOpen(true)}
          />
        </div>
      </div>
      <div className="row">
        <div className="mb-3 col-md-11">
          <Controller
            control={siteControl}
            name="manager"
            render={({ field, fieldState: { error } }) => (
              <MLSelect
                required
                label={t('Modals.manage-technician.manager')}
                error={error?.message}
                options={managerOptions}
                value={field.value != null ? String(field.value) : ''}
                onChange={val => field.onChange(val ? Number(val) : null)}
              />
            )}
          />
        </div>
        <div className="mb-3 col-md-1 mt-auto">
          <GreenAddButton
            title={t('Modals.manage-client.createNewManager')}
            onClick={() => setIsEmployeeModalOpen(true)}
          />
        </div>
      </div>
    </form>
  );

  const renderClientCreationTab = () => (
    <div className="row">
      <div className="col-12 mb-3">
        <h5>Fill Client Details</h5>
      </div>

      <div className="mb-3 col-md-4">
        <Controller
          control={clientControl}
          name="name"
          render={({ field, fieldState: { error } }) => (
            <MLInput type="text" label={t('Modals.manage-client.name')} error={error?.message} required {...field} />
          )}
        />
      </div>

      <div className="mb-3 col-md-4">
        <Controller
          control={clientControl}
          name="EDRPOU"
          render={({ field, fieldState: { error } }) => (
            <MLInput label={t('Modals.manage-client.EDRPOU')} type="text" error={error?.message} {...field} />
          )}
        />
      </div>

      <div className="mb-3 col-md-4">
        <Controller
          control={clientControl}
          name="business_structure"
          render={({ field, fieldState: { error } }) => (
            <MLInput
              label={t('Modals.manage-client.business_structure')}
              type="text"
              error={error?.message}
              {...field}
            />
          )}
        />
      </div>

      <div className="mb-3 col-md-4">
        <Controller
          control={clientControl}
          name="country"
          render={({ field, fieldState: { error } }) => (
            <MLSelect
              {...field}
              label={t('Modals.manage-client.country')}
              error={error?.message}
              options={countryList}
              formatOptionLabel={formatCountryOption}
              value={String(field.value || '')}
              onChange={val => field.onChange(val || '')}
              {...(isObjectCreating || clientId ? { required: true } : { required: false })}
            />
          )}
        />
      </div>

      <div className="mb-3 col-md-4">
        <Controller
          control={clientControl}
          name="city"
          render={({ field, fieldState: { error } }) => (
            <MLInput
              {...field}
              error={error?.message}
              label={t('Modals.manage-client.city')}
              {...(isObjectCreating || clientId ? { required: true } : { required: false })}
            />
          )}
        />
      </div>

      <div className="mb-3 col-md-4">
        <Controller
          control={clientControl}
          name="zip_code"
          render={({ field, fieldState: { error } }) => (
            <MLInput
              {...field}
              error={error?.message}
              label={t('Modals.manage-client.zip')}
              {...(isObjectCreating || clientId ? { required: true } : { required: false })}
            />
          )}
        />
      </div>

      <div className="mb-3 col-md-4">
        <Controller
          control={clientControl}
          name="street"
          render={({ field, fieldState: { error } }) => (
            <MLInput
              {...field}
              error={error?.message}
              label={t('Modals.manage-client.street')}
              {...(isObjectCreating || clientId ? { required: true } : { required: false })}
            />
          )}
        />
      </div>

      <div className="mb-3 col-md-4">
        <Controller
          control={clientControl}
          name="building_no"
          render={({ field, fieldState: { error } }) => (
            <MLInput
              {...field}
              error={error?.message}
              label={t('Modals.manage-client.building_no')}
              {...(isObjectCreating || clientId ? { required: true } : { required: false })}
            />
          )}
        />
      </div>

      <div className="mb-3 col-md-4">
        <Controller
          control={clientControl}
          name="apartment_no"
          render={({ field, fieldState: { error } }) => (
            <MLInput label={t('Modals.manage-client.apartment_no')} error={error?.message} {...field} />
          )}
        />
      </div>

      {!clientId && (
        <div className="mb-3 col-12" title={t('Modals.manage-client.tooltip-add-site')}>
          <Toggle
            checked={isObjectCreating}
            onChange={checked => setIsObjectCreating(checked)}
            label={t('Modals.manage-client.createSiteSameAsCompany')}
            size="sm"
          />
        </div>
      )}

      <div className="col-12 col-md-6 mb-3">
        <div className="d-flex align-items-center justify-content-center gap-2">
          <div className="flex-fill">
            <Controller
              control={clientControl}
              name="owner"
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <MLSelect
                  {...field}
                  required
                  label={t('Modals.manage-client.owner')}
                  error={error?.message}
                  options={ownerOptions}
                  value={String(field.value || '')}
                  onChange={val => field.onChange(val || '')}
                />
              )}
            />
            <small className="text-muted d-block mt-1" style={{ lineHeight: '1.2', fontSize: '0.85em' }}>
              Primary decision-maker and main point of contact
            </small>
          </div>

          <button
            className="btn btn-ghost-success mt-1"
            type="button"
            title="Add account Owner"
            onClick={() => setIsOwnerModalOpen(true)}
          >
            +
          </button>
        </div>
      </div>

      {isObjectCreating && !clientId && (
        <div className="col-12 col-md-6 mb-3">
          <div className="d-flex align-items-center justify-content-center gap-2">
            <div className="flex-fill">
              <Controller
                control={clientControl}
                name="technician"
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <MLSelect
                    {...field}
                    required
                    label={t('Modals.manage-technician.technician')}
                    error={error?.message}
                    options={technicianOptions}
                    value={String(field.value || '')}
                    onChange={val => field.onChange(val || '')}
                  />
                )}
              />
              <small className="text-muted d-block mt-1" style={{ lineHeight: '1.2', fontSize: '0.85em' }}>
                The technician responsible for this site. You can change this later if needed.
              </small>
            </div>
            <div className="d-flex justify-content-center align-items-center pb-1">
              <button
                title={t('Modals.manage-technician.addNew')}
                className="btn btn-ghost-success"
                type="button"
                onClick={() => setIsTechModalOpen(true)}
              >
                +
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderTabContent = () => {
    switch (currentTab) {
      case 'technicianCreation':
        return renderTechnicianCreationTab();
      default:
        return renderClientCreationTab();
    }
  };

  const renderFooterButtons = () => {
    if (currentTab === 'ownerCreation') {
      return (
        <>
          <Button
            className="btn btn-ghost-danger"
            onClick={() => {
              onResetAll();
              onClose();
            }}
          >
            {t('Modals.delete-modal.cancel')}
          </Button>

          <Button
            className="btn btn-ghost-success"
            onClick={handleClientSubmit(onCreateNewClient)}
            disabled={isCreatingClient}
          >
            {isCreatingClient ? t('Modals.manage-client.creating') : t('Modals.delete-modal.create')}
          </Button>
        </>
      );
    }

    if (currentTab === 'technicianCreation') {
      return (
        <>
          <Button
            className="btn btn-ghost-danger"
            onClick={() => {
              onResetAll();
              onClose();
            }}
          >
            {t('Modals.delete-modal.cancel')}
          </Button>

          <Button
            className="btn btn-ghost-success"
            onClick={handleSiteSubmit(onCreateNewSite)}
            disabled={isCreatingTechnician || isCreatingSite}
          >
            {isCreatingTechnician || isCreatingSite
              ? t('Modals.manage-client.creating')
              : t('Modals.delete-modal.create')}
          </Button>
        </>
      );
    }

    return (
      <Button
        className="btn btn-ghost-danger"
        onClick={() => {
          onResetAll();
          onClose();
        }}
      >
        {t('Modals.delete-modal.cancel')}
      </Button>
    );
  };

  return (
    <div>
      <Modal
        isOpen={isOpen}
        toggle={() => {
          onResetAll();
          onClose();
        }}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader
          className="border-bottom py-3"
          toggle={() => {
            onResetAll();
            onClose();
          }}
        >
          <div className="d-flex justify-content-between align-items-center w-100 gap-5">
            <div className="text-purple">
              {clientId
                ? t('Modals.choose-action.title')
                : currentTab === 'ownerCreation'
                  ? t('Modals.manage-owner.header-choose')
                  : t('Modals.manage-technician.header-choose')}
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="py-3">{renderTabContent()}</ModalBody>
        <ModalFooter>{renderFooterButtons()}</ModalFooter>
      </Modal>

      {/* Owner modal: refetch + auto-select */}
      <CreateOwnerModal
        isOpen={isOwnerModalOpen}
        onClose={() => {
          setIsOwnerModalOpen(false);
          refetchClientOwners();
        }}
      />

      {/* Technician modal: refetch + auto-select (client form) */}
      <CreateTechnicianModal
        isClientModal={currentTab === 'ownerCreation'}
        isOpen={isTechModalOpen}
        onClose={() => {
          setIsTechModalOpen(false);
          refetchAvailableTechnicians();
        }}
      />

      {/* Employee modal: refetch + auto-select (site form) */}
      <CreateEmployeeModal
        isAddingEmployee
        isOpen={isEmployeeModalOpen}
        onClose={() => {
          setIsEmployeeModalOpen(false);
          refetchAvailableManagers();
        }}
      />
    </div>
  );
};

export default ChooseModal;
